import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";

import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import { isEqual } from "date-fns";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";

import {
  getAllPetsByWorkspaceId,
  getPetNamesByWorkspaceId,
  setArchieve,
} from "../../Api/Services/PetServices.js";
import "./GlobalItemList.css";
import { ReactComponent as ViewIcon } from "../../Assets/Images/viewIcon.svg";

import useAuth from "../../ContextProvider/AuthProvider";
import { filterAscDescData, formatDate, hexToRgba } from "../../Utils.js";
import DetailsModal from "../Common/DetailsModal.js";
import PawItemList from "../Common/PawItemList.js";
import { Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";

export default function PetInformation() {
  const theme = useTheme();
  const { workspaceId: w_id } = useAuth();
  const [filter, setFilter] = useState({});
  const [pageCount, setPageCount] = useState(0);
  const [petData, setPetData] = useState([]);

  const navigate = useNavigate();
  const [selectedPetName, setSelectedPetName] = useState("");
  const fetchIdRef = useRef(0);

  const { data = [], isLoading } = useSWR(
    w_id ? `Petdata${w_id}` : null,
    async () => {
      const { limit } = filter;
      const { data } = await getAllPetsByWorkspaceId(w_id, filter);
      if (data?.data?.length && limit) {
        setPageCount(Math.ceil((data?.data?.length + 1) / limit));
      }
      return data?.data || [];
    },
    {
      revalidateOnFocus: false,
    }
  );

  const { data: petNames = [], isLoading: isPetLoading } = useSWR(
    w_id ? `petNames${w_id}` : null,
    async () => {
      const { data } = await getPetNamesByWorkspaceId(w_id);
      return data?.data || [];
    },
    {
      revalidateOnFocus: false,
    }
  );

  const fetchData = useCallback((oFilter) => {
    const fetchId = ++fetchIdRef.current;
    if (fetchId === fetchIdRef.current && !isEqual(filter, oFilter)) {
      setFilter(oFilter);
    }
  }, []);

  useEffect(() => {
    const { limit, from, sort } = filter;
    if (filter && filter.from > -1) {
      if (data?.length && limit) {
        setPageCount(Math.ceil(data?.length / limit));
        const allData = [...data];
        const filteredResult = filterAscDescData(allData, sort, from, limit);
        setPetData(filteredResult);
      }
    }
  }, [filter, data]);

  const result = petData?.length
    ? petData?.map((pet) => {
        const { id, name, family_name, dob_date, image, is_archieved } = pet;

        return {
          id,
          petId: id,
          family_name: family_name,
          dob_date: formatDate(dob_date),
          petImage: image,
          name,
          is_archieved,
        };
      })
    : [];

  useEffect(() => {}, [selectedPetName]);

  const filteredData =
    selectedPetName === ""
      ? result
      : result.filter((item) => {
          const petName = item.name.toLowerCase() || "";
          const query = selectedPetName.toLowerCase();
          return petName.includes(query);
        });

  const petNamesSet = new Set();
  const filteredPetNames = petNames.filter((pet) => {
    const lowercaseName = pet.name.toLowerCase();
    if (!petNamesSet.has(lowercaseName)) {
      petNamesSet.add(lowercaseName);
      return true;
    }
    return false;
  });

  const [open, setOpen] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [archivePetId, setArchivePetId] = useState(null);

  const handleArchiveClick = (petId) => {
    setShowSuccessMessage(true);
    setOpen(true);
    setArchivePetId(petId); // Store the petId in a state variable
  };
  const handleArchive = () => {
    setIsUpdating(true);

    setArchieve(archivePetId) // Use the stored petId from state
      .then((response) => {
        setIsUpdating(false);
        console.log(response);
        if (response.data.success === true) {
          window.location.reload();
        }
      })
      .catch((err) => {
        console.error(err);
        setOpen(true);
        setIsUpdating(false);
      });
  };

  const columns = useMemo(() => {
    return [
      {
        Header: "",
        accessor: "petImage",
        width: "8vw",
        disableSortBy: true,
        Cell: ({ row }) => {
          return row?.original?.petImage ? (
            <img
              alt="petImage"
              src={row?.original?.petImage}
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "10px",
                objectFit: "cover",
                margin: "0 auto",
                verticalAlign: "middle",
              }}
            />
          ) : (
            <AccountCircleIcon
              sx={{
                width: "50px",
                height: "50px",
              }}
            />
          );
        },
      },
      {
        Header: "Pet Name",
        accessor: "name",
        width: "12.5vw",
      },
      {
        Header: `Family Name`,
        accessor: "family_name",
        width: "12.5vw",
      },
      {
        Header: "Date of Birth",
        accessor: "dob_date",
        width: "12.5vw",
      },
      {
        Header: "",
        accessor: "id",
        width: "8vw",
        isArchive: true,
        disableSortBy: true,
        onClick: (item) => handleArchiveClick(item.petId),
        disabled: isUpdating,
        textAlign: "center",
        Cell: ({ row }) => {
          return (
            <UnarchiveIcon
              style={{
                color: "#000000",
              }}
              onClick={() => handleArchiveClick(row?.original?.petId)}
            />
          );
        },
      },
      {
        Header: "Action",
        accessor: "action",
        width: "8vw",
        disableSortBy: true,
        textAlign: "center",
        Cell: ({ row }) => {
          return (
            <Button
              sx={{
                minWidth: "40px",
                background: `${hexToRgba(
                  theme.palette.primary.main,
                  0.2
                )} !important`,
                borderRadius: "10px",
                marginRight: "5px",
                width: "24px",
              }}
              onClick={() => navigate(props.viewDataLink(row.original))}
            >
              <ViewIcon
                style={{
                  fill: `${theme.palette.primary.main}`, 
                  cursor: "pointer",
                }}
                alt="view"
              />
            </Button>
          );
        },
      },
    ];
  }, []);

  const defaultOptions = {
    pageSize: 5,
  };

  const props = {
    label: "Pet Information",
    setSelectedUserName: setSelectedPetName,
    items: filteredPetNames,
    isLoading: isLoading || isPetLoading,
    dataAddLink: "/pet-information/form",
    columns: columns,
    columnsData: filteredData,
    viewDataLink: (data) => `/pet-information/info/${data.petId}`,
    placeholder: "pet name",
    details: "  There are no pet details for this user.",
    isSelectHide: true,
    fetchData,
    pageCount,
    defaultOptions,
  };
  const ButtonItem = [
    {
      name: "yes",
      onClick: () => {
        handleArchive(archivePetId);
        setShowSuccessMessage(false);
      },
    },
    {
      name: "no",
      onClick: () => {
        setOpen(false);
        setShowSuccessMessage(false);
      },
    },
  ];

  return (
    <>
      <PawItemList {...props} />
      {showSuccessMessage && (
        <>
          <DetailsModal
            open={open}
            severity="warning"
            buttonList={ButtonItem}
            title="Do you want to archive this pet?"
            handleClose={() => setOpen(false)}
          />
        </>
      )}
    </>
  );
}
